import { computed, Injectable, signal, WritableSignal } from "@angular/core";
import { HttpTransportType, HubConnection, HubConnectionBuilder, HubConnectionState } from "@microsoft/signalr";
import { firstValueFrom } from "rxjs";
import { Constants } from "../../../constants";
import { INotification } from "../../interfaces";
import { ArcusAuthService } from "../../core/arcusauth.service";
import ExceptionService from "./exception.service";
import ToastService from "./toast.service";

function isIOS(): boolean {
    const browserInfo = navigator.userAgent.toLowerCase();
    if (browserInfo.match("iphone") || browserInfo.match("ipad")) {
        return true;
    }
    return (
        ["iPad Simulator", "iPhone Simulator", "iPod Simulator", "iPad", "iPhone", "iPod"].includes(
            navigator.platform
        ) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
    );
}

function isAndroid(): boolean {
    return /android/i.test(navigator.userAgent);
}

@Injectable({
    providedIn: "root",
})
export default class NotificationV2Service {
    static readonly FRAUD_METADATA_KEY = "isFraud";

    private connection: HubConnection;
    private desktopNotifications: boolean;

    private readonly notificationsSignal: WritableSignal<INotification[]> = signal([]);
    readonly notifications = this.notificationsSignal.asReadonly();
    readonly notificationCount = computed(() => this.notifications().length);

    readonly fraudAlerts = computed(() =>
        this.notifications().filter((x) => x.Metadata?.[NotificationV2Service.FRAUD_METADATA_KEY] === "true")
    );
    readonly fraudAlertCount = computed(() => this.fraudAlerts().length);

    readonly connectionState = signal<HubConnectionState>(HubConnectionState.Disconnected);

    constructor(
        private authService: ArcusAuthService,
        private exceptionService: ExceptionService,
        private toastr: ToastService
    ) {
        try {
            this.desktopNotifications = Notification && Notification.permission === "granted";
        } catch (err) {
            // this is to catch if ios device and doesn't have Notification Api
            // also in case something goes wrong we don't want the site to not load, just because notifications won't work
            console.error(err);
            this.desktopNotifications = false;
        }

        // Keep the hub connection lifecycle in sync with auth state so we never hold an
        // authenticated connection open past logout, or miss a connection after login.
        this.authService.isAuthenticated$.subscribe((authenticated) => {
            if (authenticated) {
                this.startConnection();
            } else {
                this.stopConnection();
            }
        });
    }

    private startConnection(): void {
        if (this.connection) {
            return;
        }
        this.checkDesktopNotificationPermission();

        this.connection = new HubConnectionBuilder()
            .withUrl(`${Constants.apiEndpoint}hubs/notifications`, {
                transport: HttpTransportType.WebSockets,
                accessTokenFactory: () => firstValueFrom(this.authService.getAccessToken()),
            })
            .withAutomaticReconnect([0, 2000, 5000, 10000, 30000, 60000])
            .build();

        this.registerOnEvents();

        this.connection.onreconnecting(() => this.connectionState.set(HubConnectionState.Reconnecting));
        this.connection.onreconnected(() => this.connectionState.set(HubConnectionState.Connected));
        this.connection.onclose(() => {
            this.connectionState.set(HubConnectionState.Disconnected);
            this.connection = undefined;
        });

        this.connection
            .start()
            .then(() => this.connectionState.set(HubConnectionState.Connected))
            .catch((err) => {
                this.toastr.error("Something went wrong. Live notifications are not currently available.");
                console.error(err);
            });
    }

    private stopConnection(): void {
        if (!this.connection) {
            return;
        }
        this.connection.stop();
        this.connection = undefined;
        this.connectionState.set(HubConnectionState.Disconnected);
        this.notificationsSignal.set([]);
    }

    private registerOnEvents(): void {
        this.connection.on("ReceiveInitialNotifications", (notifications: INotification[]) => {
            this.notificationsSignal.set(notifications ?? []);
        });

        this.connection.on("ReceiveNotification", (notification: INotification) => {
            this.notificationsSignal.update((current) => [notification, ...current]);
            if (this.desktopNotifications) {
                this.createDesktopNotification(notification);
            }
        });

        this.connection.on("NotificationDismissed", (notificationId: number) => {
            this.notificationsSignal.update((current) => current.filter((x) => x.ID !== notificationId));
        });

        this.connection.on("AllNotificationsDismissed", () => {
            this.notificationsSignal.set([]);
        });
    }

    dismissNotification = (notification: INotification): void => {
        const previous = this.notificationsSignal();
        this.notificationsSignal.update((current) => current.filter((x) => x.ID !== notification.ID));

        this.connection?.invoke("DismissNotification", notification.ID).catch((error) => {
            this.notificationsSignal.set(previous);
            this.exceptionService.handleException(error);
        });
    };

    dismissAllNotifications = (): void => {
        const previous = this.notificationsSignal();
        this.notificationsSignal.set([]);

        this.connection?.invoke("DismissAllNotifications").catch((error) => {
            this.notificationsSignal.set(previous);
            this.exceptionService.handleException(error);
        });
    };

    private checkDesktopNotificationPermission(): void {
        if (isIOS()) {
            this.desktopNotifications = false;
            return;
        }
        if (!this.desktopNotifications && Notification && Notification.permission !== "denied") {
            Notification.requestPermission().then((result) => {
                this.desktopNotifications = result === "granted";
            });
        }
    }

    private createDesktopNotification(notification: INotification): void {
        const title = notification.Title || "New notification received";
        const options = {
            body: notification.Body || "You have received a new notification in Arcus.",
            icon: Constants.baseURL + "assets/images/arcus-logo-vertical.png",
        };

        // Android Chrome does not support the Notification constructor - must use Service Worker API
        if (isAndroid() && navigator.serviceWorker) {
            navigator.serviceWorker.ready
                .then((registration) => registration.showNotification(title, options))
                .catch((err) => console.error(err));
            return;
        }

        try {
            const desktopNotification = new Notification(title, options);
            desktopNotification.onclick = (e: Event) => {
                window.focus();
                (e.target as Notification).close();
            };
        } catch (err) {
            console.error(err);
        }
    }
}
